import { useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Calendar, Mail } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/business-prulomedia/30min";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message sent. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", role: "", message: "" });
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHeader
        eyebrow="Contact"
        title="Let's build your next hit."
        description="Book a 30-minute strategy call, or send us a message. We reply to every inquiry within 24 hours."
      />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="border border-white/10 rounded-3xl p-8 md:p-10 bg-zinc-950/40" data-testid="contact-booking">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-zinc-500">
              <Calendar size={14} /> Book a call
            </div>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight">30-minute strategy call</h2>
            <p className="mt-4 text-zinc-400">
              We'll walk through your upcoming release, current audience, and how Prulo would approach the campaign.
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calendly-link"
              className="mt-8 inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3.5 hover:bg-zinc-200 btn-magnetic"
            >
              Open Calendly
            </a>
            <div className="mt-10 pt-10 border-t border-white/10 flex items-center gap-3 text-sm text-zinc-400">
              <Mail size={14} /> Business@prulomedia.com
            </div>
          </div>

          <form onSubmit={submit} className="border border-white/10 rounded-3xl p-8 md:p-10 bg-zinc-950/40" data-testid="contact-form">
            <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-4">Or send us a message</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Quick inquiry</h2>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input required data-testid="contact-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-white/10 h-12" />
              <Input required type="email" data-testid="contact-email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border-white/10 h-12" />
              <Input data-testid="contact-company" placeholder="Company / label" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-transparent border-white/10 h-12" />
              <Input data-testid="contact-role" placeholder="Your role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-transparent border-white/10 h-12" />
              <Textarea required data-testid="contact-message" placeholder="Tell us about your release" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-transparent border-white/10 sm:col-span-2 min-h-32" />
            </div>

            <button type="submit" disabled={submitting} data-testid="contact-submit" className="mt-6 bg-white text-black font-semibold rounded-full px-6 py-3.5 hover:bg-zinc-200 transition-all disabled:opacity-50">
              {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
